/**
 * Extracts the primary phone number from an applicant object,
 * handling all product-specific data shapes.
 *
 * @param {Object} applicant - The applicant object
 * @returns {string|null} Formatted phone number (e.g., "+40765432343") or null
 */
export function extractPrimaryPhone(applicant) {
  if (!applicant) return null;

  const sources = [
    // Variation 1-3: phoneNumber (string or object) + optional phoneCode/countryCode
    () => extractFromPhoneNumber(applicant),
    // Variation 4: ESTA — contactInfo array
    () => extractFromArray(applicant.contactInfo, "phoneNumber", "countryCode"),
    // Variation 5: GE/NEXUS/SENTRI — phones array
    () => extractFromArray(applicant.phones, "phoneNumber", "countryCode"),
    // Variation 6: Australia old — homeTelephone/mobileTelephone/businessTelephone
    () => extractFromNestedObject(applicant.mobileTelephone),
    () => extractFromNestedObject(applicant.homeTelephone),
    () => extractFromNestedObject(applicant.businessTelephone),
    // Variation 7: DS-160 — phonePrimary + phonePrimaryCode (or nested)
    () => formatPhone(applicant.phonePrimaryCode || applicant.phonePrimaryNumber?.phonePrimaryCode, applicant.phonePrimary || applicant.phonePrimaryNumber?.phonePrimary),
    // Variation 8: TSA — telephoneNumber + telephoneNumberCountryCode
    () => formatPhone(applicant.telephoneNumberCountryCode, applicant.telephoneNumber),
    // Variation 9: New Zealand — contactInformation object
    () => extractFromNestedObject(applicant.contactInformation),
    // Variation 10: UK/Egypt/AusV2 — userPhone
    () => formatPhone(null, applicant.userPhone),
    // Variation 11: Singapore — mobileNumber + countryCode
    () => formatPhone(applicant.countryCode, applicant.mobileNumber),
  ];

  for (const source of sources) {
    const result = source();
    if (result) return result;
  }

  return null;
}

function extractFromPhoneNumber(applicant) {
  const phoneField = applicant.phoneNumber;
  if (!phoneField) return null;

  if (phoneField !== null && typeof phoneField === "object") {
    return formatPhone(phoneField.countryCode, phoneField.number);
  }

  return formatPhone(applicant.phoneCode || applicant.countryCode, phoneField);
}

function extractFromArray(arr, phoneKey, codeKey) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = arr[0];
  if (!first) return null;
  return formatPhone(first[codeKey], first[phoneKey]);
}

function extractFromNestedObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  return formatPhone(obj.countryCode, obj.phoneNumber);
}

/**
 * Formats a country code + phone number into a dial-ready string.
 * Handles code formats: "40:RO", "+40", "40", null
 */
function formatPhone(rawCode, rawNumber) {
  if (!rawNumber || typeof rawNumber !== "string") return null;
  const code = parseCountryCode(rawCode);
  const formatted = code ? `+${code}${rawNumber}` : rawNumber;
  return formatted.trim() || null;
}

function parseCountryCode(raw) {
  if (!raw || typeof raw !== "string") return "";
  const cleaned = raw.split(":")[0].replace(/[^\d]/g, "").trim();
  return cleaned;
}
